import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { BottomSheet } from './BottomSheet.jsx'
import { useEvenI18n } from '../i18n/even.js'

// Dialog di conferma/notifica custom (sostituisce window.confirm/alert):
// stesso BottomSheet del resto dell'app, promise-based.
//
//   const { confirm, notify } = useConfirm()
//   if (await confirm({ message, okLabel?, cancelLabel?, danger? })) { … }
//   await notify({ message, okLabel? })

const Ctx = createContext(null)

export function ConfirmProvider({ children }) {
  const t = useEvenI18n()
  const [dialog, setDialog] = useState(null)
  const resolveRef = useRef(null)

  const ask = useCallback((opts) => new Promise(resolve => {
    resolveRef.current = resolve
    setDialog(opts)
  }), [])

  const confirm = useCallback((opts) => ask({ ...opts, notifyOnly: false }), [ask])
  const notify  = useCallback((opts) => ask({ ...opts, notifyOnly: true }), [ask])

  const close = useCallback((result) => {
    const resolve = resolveRef.current
    resolveRef.current = null
    setDialog(null)
    resolve?.(result)
  }, [])

  return (
    <Ctx.Provider value={{ confirm, notify }}>
      {children}
      <BottomSheet open={Boolean(dialog)} onClose={() => close(false)} className="bsheet-dialog">
        {dialog && (
          <div className="confirm-sheet">
            {dialog.title && <h3 className="confirm-title">{dialog.title}</h3>}
            <p className="confirm-message">{dialog.message}</p>
            <div className="confirm-actions">
              {!dialog.notifyOnly && (
                <button type="button" className="btn btn-ghost" onClick={() => close(false)}>
                  {dialog.cancelLabel ?? t.common.cancel}
                </button>
              )}
              <button
                type="button"
                className={`btn ${dialog.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => close(true)}
                autoFocus
              >
                {dialog.okLabel ?? (dialog.notifyOnly ? t.common.ok : t.common.confirm)}
              </button>
            </div>
          </div>
        )}
      </BottomSheet>
    </Ctx.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useConfirm outside ConfirmProvider')
  return ctx
}
